import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IteroBrowserTab } from './browserTabs';
import {
    NO_SELECTION,
    parentSelection,
    sameSelection,
    type IteroBrowserRoute,
    type IteroSelection
} from './iteroBrowserRoute';
import { useIteroBrowserRoute, type IteroBrowserRouteState } from './useIteroBrowserRoute';

export interface IteroTabNav {
    selection: IteroSelection;
    push: (next: IteroSelection) => void;
    replace: (next: IteroSelection) => void;
    stepBack: () => void;
}

export interface IteroBrowserNavigation {
    activeTab: IteroBrowserTab;
    selectTab: (tab: IteroBrowserTab) => void;
    patients: IteroTabNav;
    orders: IteroTabNav;
}

type TabSelections = Readonly<Record<IteroBrowserTab, IteroSelection>>;

const CLOSED_FALLBACK: IteroBrowserRoute = { tab: 'patients', selection: NO_SELECTION };

function initialMemory(route: IteroBrowserRoute): TabSelections {
    return { patients: NO_SELECTION, orders: NO_SELECTION, [route.tab]: route.selection };
}

// Rewrites a hand-edited or stale URL once per parse (StrictMode re-runs effects), keeping its state.
function useCanonicalUrl(routeState: IteroBrowserRouteState): void {
    const { route, isCanonical, replace } = routeState;
    const handled = useRef<IteroBrowserRoute | null>(null);

    useEffect(() => {
        if (isCanonical || route === null || handled.current === route) return;
        handled.current = route;
        replace(route);
    }, [isCanonical, route, replace]);
}

// The hidden tab keeps its last selection in memory, so switching back restores it like before.
function useTabMemory(route: IteroBrowserRoute): TabSelections {
    const [memory, setMemory] = useState<TabSelections>(() => initialMemory(route));
    const { tab, selection } = route;

    useEffect(() => {
        setMemory((current) =>
            sameSelection(current[tab], selection) ? current : { ...current, [tab]: selection }
        );
    }, [tab, selection]);

    return memory;
}

function useTabNav(
    routeState: IteroBrowserRouteState,
    tab: IteroBrowserTab,
    selection: IteroSelection
): IteroTabNav {
    const { push, replace, stepBack } = routeState;
    return useMemo(
        () => ({
            selection,
            push: (next) => push({ tab, selection: next }),
            replace: (next) => replace({ tab, selection: next }),
            stepBack: () => stepBack({ tab, selection: parentSelection(selection) })
        }),
        [tab, selection, push, replace, stepBack]
    );
}

export function useIteroBrowserNavigation(): IteroBrowserNavigation {
    const routeState = useIteroBrowserRoute();
    // Only mounted while open; the fallback covers the render in which Back closes it.
    const route = routeState.route ?? CLOSED_FALLBACK;
    useCanonicalUrl(routeState);
    const memory = useTabMemory(route);
    const activeTab = route.tab;
    const selectionFor = (tab: IteroBrowserTab) =>
        tab === activeTab ? route.selection : memory[tab];
    const patients = useTabNav(routeState, 'patients', selectionFor('patients'));
    const orders = useTabNav(routeState, 'orders', selectionFor('orders'));
    const { replace } = routeState;

    const selectTab = useCallback(
        (tab: IteroBrowserTab) => {
            if (tab !== activeTab) replace({ tab, selection: memory[tab] });
        },
        [activeTab, memory, replace]
    );

    return { activeTab, selectTab, patients, orders };
}
